import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { handleLogoutStorage } from '../../utils/authStorage';
import drop_down from '../../../shared/assets/svg/drop_down_white.svg';
import socket from '../../../../socket';
import toast from 'react-hot-toast';
import { getUserById } from '../../../builder/services/api/implementation/impl';
import logoIcon from '../../../shared/assets/svg/LOGO-ICON.svg';
import { ChevronRight } from 'lucide-react';

const Sidebar = ({ role, links, logo, collapsed, setCollapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const DesignationName = Cookies.get('userDesignation'); // Getting the designation from cookies
  const userVertical = Cookies.get('userVertical'); // Getting the vertical from cookies

  const [selectedPath, setSelectedPath] = useState(location.pathname);
  const [expandedItem, setExpandedItem] = useState('');

  const handleLogout = useCallback(() => {
    handleLogoutStorage();
    navigate('/');
  }, [navigate]);

  const handleLinkClick = useCallback(
    (path) => {
      if (path === 'logout') {
        handleLogout();
      } else {
        setSelectedPath(path);
        navigate(path);
      }
    },
    [navigate, handleLogout]
  );

  useEffect(() => {
    setSelectedPath(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const handlePost = async (res) => {
      try {
        const userId = Cookies.get('userId');
        if (!userId) return;

        const userResponse = await getUserById(userId);
        const userCompanies = userResponse?.companies || [];

        if (userCompanies.includes(res.company)) {
          toast.success('New Post Received from your company');
        }
      } catch (error) {
        console.error('Error fetching user details in handlePost:', error);
      }
    };

    const handleComment = (res) => {
      const userId = Cookies.get('userId');
      const createdBy = res.post.created_by;

      if (String(userId) === String(createdBy)) {
        toast.success('Someone commented on your post');
      }
    };

    const handleTag = (res) => {
      // Uncomment if you want tag notification
      // const userId = Cookies.get('userId');
      // const createdBy = res.post.created_by;
      // if (String(userId) === String(createdBy)) {
      //   toast.success('Someone tagged on your post');
      // }
    };

    const handleReply = (res) => {
      const userId = Cookies.get('userId');
      const createdBy = res.comment.created_by;

      if (String(userId) === String(createdBy)) {
        toast.success('Someone replied on your comment');
      }
    };

    const handleLike = (res) => {
      const userId = Cookies.get('userId');
      const createdBy = res.post.created_by;

      if (String(userId) === String(createdBy)) {
        toast.success(`${res.uerData?.name} liked your post`);
      }
    };

    socket.on('Post', handlePost);
    socket.on('Comment', handleComment);
    socket.on('Reply', handleReply);
    socket.on('Like', handleLike);
    socket.on('TagNotification', handleTag);

    return () => {
      socket.off('Post', handlePost);
      socket.off('Comment', handleComment);
      socket.off('Reply', handleReply);
      socket.off('Like', handleLike);
      socket.off('TagNotification', handleTag);
    };
  }, []);

  const renderLinks = (links) => {
    const filteredLinks = links.filter((link) => {
      // Allow Integration for Admin, Management, Marketing, and Inventory verticals
      if (link.name === 'Integration') {
        return (
          DesignationName === 'Admin' ||
          userVertical?.toLowerCase() === 'management' ||
          userVertical?.toLowerCase() === 'marketing' ||
          userVertical?.toLowerCase() === 'inventory'
        );
      }
      return true;
    });

    return filteredLinks.map((link, index) => (
      <li
        key={index}
        className={`flex flex-col justify-center items-start mb-2 p-3 ${
          selectedPath.includes(link.path) && 'rounded-lg bg-bgSidebar'
        }`}>
        <div className="flex w-full justify-between items-center space-x-2">
          <div
            className="flex items-center space-x-1 cursor-pointer gap-2"
            onClick={() => {
              if (collapsed) {
                // Expand first, then navigate
                setCollapsed(false);
                setTimeout(() => handleLinkClick(link.path), 300); // match with transition duration
              } else {
                handleLinkClick(link.path);
              }
            }}>
            <img
              src={link.icon}
              alt={link.name}
              className="h-5 w-5 flex-shrink-0 transition-all duration-300 ease-in-out"
            />
            <div
              className={`text-white hover:text-gray-300 cursor-pointer whitespace-nowrap overflow-hidden transition-all duration-300 ease-in-out ${
                collapsed ? 'opacity-0 max-w-0' : 'opacity-100 max-w-[200px] ml-2'
              }`}>
              {link.name}
            </div>
          </div>

          {/* Drop down toggle icon */}
          {link?.children && !collapsed && (
            <img
              src={drop_down}
              alt="Drop down icon"
              className={`cursor-pointer transform transition-all duration-300 ease-in-out ${
                expandedItem === link ? 'rotate-180' : ''
              }`}
              onClick={(e) => {
                e.stopPropagation(); // prevent triggering parent onClick
                setExpandedItem(expandedItem === link ? '' : link);
              }}
            />
          )}
        </div>

        <div
          className={`flex flex-col ml-9 overflow-hidden transition-all duration-300 ease-in-out gap-2 ${
            expandedItem === link && !collapsed
              ? 'max-h-[500px] opacity-100 mt-3'
              : 'max-h-0 opacity-0 mt-0 pointer-events-none'
          }`}>
          {link?.children?.map((item, idx) => {
            const displayName = typeof item === 'object' ? item.displayName : item;
            const pathSegment = typeof item === 'object' ? item.path : item;
            return (
              <span
                key={idx}
                className={`cursor-pointer ${
                  selectedPath === `${link.path}/${pathSegment.toLowerCase()}`
                    ? 'text-[#77BDC5]'
                    : 'text-white'
                }`}
                onClick={() => handleLinkClick(`${link.path}/${pathSegment.toLowerCase()}`)}>
                {displayName}
              </span>
            );
          })}
        </div>
      </li>
    ));
  };

  return (
    <div
      className={`sidebar fixed z-[1000] top-0 left-0 bg-customBlue min-h-screen border-r border-gray-300
        ${collapsed ? 'w-[76px]' : 'w-64 md:w-64'}
       transition-all duration-300 ease-in-out
       md:block
      `}
      onMouseEnter={() => {
        if (role !== 'admin') setCollapsed(false);
      }}
      onMouseLeave={() => {
        if (role !== 'admin') setCollapsed(true);
      }}>
      <div className="flex items-center justify-between p-4 relative">
        <div className="relative h-[3.5rem] w-[11rem] flex items-center justify-center">
          <img
            src={logoIcon}
            alt="LogoIcon"
            className={`absolute top-0 left-0 transition-all duration-300 ease-in-out
              ${collapsed ? 'opacity-100 scale-100 h-[2.5rem] w-[3.3rem]' : 'opacity-0 scale-90 h-0 w-0'}
            `}
            style={{ objectFit: 'contain' }}
          />
          <img
            src={logo}
            alt="Logo"
            className={`absolute top-0 left-0 transition-all duration-300 ease-in-out
              ${collapsed ? 'opacity-0 scale-90 h-0 w-0' : 'opacity-100 scale-100 h-[3.5rem] w-[11rem]'}
            `}
            style={{ objectFit: 'contain' }}
          />
        </div>
        {role !== 'admin' && (
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="absolute top-2 right-[-0.6rem] w-6 h-6 flex items-center justify-center bg-[#2A565E] text-white rounded-full z-30"
            aria-label="Toggle Sidebar">
            <ChevronRight
              className={`w-4 h-4 transition-transform duration-300 ${!collapsed ? 'rotate-180' : ''}`}
            />
          </button>
        )}
      </div>

      <ul className="p-4 overflow-y-auto overflow-x-hidden hide-scrollbar max-h-[calc(100vh-5rem)] ">
        {renderLinks(links)}
      </ul>
    </div>
  );
};

export default Sidebar;
